import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { TbCalendarEvent, TbChartBar, TbChevronRight, TbClipboardList } from "react-icons/tb";
import Button from "../Button/Button";
import SelectQuestionBankModal from "../Modal/SelectQuestionBankModal";
import {
  DashboardStat,
  EMPTY_PLATFORM_SUMMARY,
  PlatformSummaryResponse,
} from "../../constants/dashboardStats";
import { Class } from "../../types";
import { openUpgradeModal } from "../../lib/upgradeModal";
import "./DashboardStatsBanner.scss";

type DashboardStatsBannerProps = {
  name: string;
  studentView: boolean;
  teacherStats: DashboardStat[];
  platformSummary?: PlatformSummaryResponse;
  enrolments?: Class[];
  /** Free-trial users don't get statistics, so the stats panel is replaced by an upgrade prompt. */
  isTrial?: boolean;
  trialEnded?: boolean;
};

type BannerHighlight = {
  key: string;
  label: string;
  value: ReactNode;
  detail: string;
  icon: ReactNode;
  accent: "blue" | "green" | "neutral";
  progress?: { current: number; total: number };
};

const DashboardStatsBanner = ({
  name,
  studentView,
  teacherStats,
  platformSummary = EMPTY_PLATFORM_SUMMARY,
  enrolments = [],
  isTrial = false,
  trialEnded = false,
}: DashboardStatsBannerProps) => {
  const navigate = useNavigate();
  const [showQuestionBankModal, setShowQuestionBankModal] = useState(false);

  // Statistics aren't part of the free trial, so trial banks are left out of the picker.
  const statsEnrolments = enrolments.filter((e) => !e.isTrial);

  const handleViewStatistics = () => {
    if (statsEnrolments.length === 0) {
      toast.error("You are not enrolled in any question banks yet.");
      return;
    }

    if (statsEnrolments.length === 1) {
      navigate(`/stats?subjectId=${statsEnrolments[0].subjectId}`);
      return;
    }

    setShowQuestionBankModal(true);
  };

  const handleQuestionBankSelect = (subjectId: number) => {
    setShowQuestionBankModal(false);
    navigate(`/stats?subjectId=${subjectId}`);
  };

  const highlights: BannerHighlight[] = studentView
    ? [
        {
          key: "questions",
          label: "Questions",
          value: platformSummary.questionsThisWeek.toLocaleString(),
          detail: "answered in the last 7 days",
          icon: <TbClipboardList aria-hidden="true" />,
          accent: "blue",
        },
        {
          key: "active-days",
          label: "Active days",
          value: (
            <>
              <span className="dashboard-stats-banner__stat-value-main">
                {platformSummary.activeDaysThisWeek}
              </span>
              <span className="dashboard-stats-banner__stat-value-denom">/ 7</span>
            </>
          ),
          detail: "days with practice this week",
          icon: <TbCalendarEvent aria-hidden="true" />,
          accent: "green",
          progress: {
            current: platformSummary.activeDaysThisWeek,
            total: 7,
          },
        },
      ]
    : teacherStats.map((stat, index) => ({
        key: stat.label,
        label: stat.label,
        value: stat.value,
        detail: stat.subtitle,
        icon: <TbChartBar aria-hidden="true" />,
        accent: index === 2 ? ("green" as const) : ("neutral" as const),
      }));

  return (
    <section className="dashboard-stats-banner">
      <div className="dashboard-stats-banner__content">
        <div className="dashboard-stats-banner__intro">
          <h3 className="dashboard-stats-banner__title">Welcome back, {name}!</h3>
          {studentView && isTrial && trialEnded ? (
            <p className="dashboard-stats-banner__subtitle">
              Your free trial has ended. Open your question bank below to review your answers, or
              upgrade for full access.
            </p>
          ) : studentView && isTrial ? (
            <p className="dashboard-stats-banner__subtitle">
              You&apos;re on a free trial. Open your question bank below to start quick practice,
              or upgrade any time for full access.
            </p>
          ) : studentView ? (
            <p className="dashboard-stats-banner__subtitle">
              Continue building your clinical knowledge. Consistent practice is the key to exam
              success.
            </p>
          ) : (
            <p className="dashboard-stats-banner__subtitle">
              Monitor your students&apos; performance and assign targeted MCQ practice to reinforce
              key ophthalmology concepts.
            </p>
          )}
          {studentView && isTrial ? (
            <div className="dashboard-stats-banner__cta">
              <Button
                text="Upgrade to full access"
                trailingIcon={<TbChevronRight aria-hidden="true" />}
                type="stats"
                onClick={openUpgradeModal}
              />
            </div>
          ) : studentView ? (
            <div className="dashboard-stats-banner__cta">
              <Button
                text="View all statistics"
                icon={<TbChartBar aria-hidden="true" />}
                trailingIcon={<TbChevronRight aria-hidden="true" />}
                type="stats"
                onClick={handleViewStatistics}
              />
            </div>
          ) : null}
        </div>

        {studentView && isTrial ? null : (
        <div className="dashboard-stats-banner__stats-panel">
          <div className="dashboard-stats-banner__stats" aria-label="Quick stats summary">
            {highlights.map((highlight) => (
              <div
                className={`dashboard-stats-banner__stat dashboard-stats-banner__stat--${highlight.accent}`}
                key={highlight.key}
              >
                <div className="dashboard-stats-banner__stat-icon">{highlight.icon}</div>
                <div className="dashboard-stats-banner__stat-body">
                  <div className="dashboard-stats-banner__stat-label">{highlight.label}</div>
                  <div className="dashboard-stats-banner__stat-value">{highlight.value}</div>
                  <div className="dashboard-stats-banner__stat-detail">{highlight.detail}</div>
                  {highlight.progress ? (
                    <div
                      className="dashboard-stats-banner__stat-progress"
                      role="progressbar"
                      aria-valuenow={highlight.progress.current}
                      aria-valuemin={0}
                      aria-valuemax={highlight.progress.total}
                      aria-label={`${highlight.progress.current} of ${highlight.progress.total} active days`}
                    >
                      <div
                        className="dashboard-stats-banner__stat-progress-fill"
                        style={{
                          width: `${Math.min(
                            100,
                            (highlight.progress.current / highlight.progress.total) * 100
                          )}%`,
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>

      <SelectQuestionBankModal
        show={showQuestionBankModal}
        onHide={() => setShowQuestionBankModal(false)}
        enrolments={statsEnrolments}
        onSelect={handleQuestionBankSelect}
      />
    </section>
  );
};

export default DashboardStatsBanner;
